import type { ReactNode } from "react";

import { ErrorBox, type LoadFailure } from "@/components/Spinner";

import { Page } from "./Page";
import { LoadingRegion, Skeleton } from "./Skeleton";

/**
 * The page of an item that is being loaded — or could not be — in an editor. A bare spinner
 * returned in place of the whole page takes the header with it, and with it the answer to "what am
 * I looking at, and did I click the right one?": here the page's own `PageHeader` (`header`, the
 * same element the loaded page draws) is already there — its title comes from the address, not from
 * the data — and only the form is missing, drawn as bars of the size it will have.
 *
 * With `failure` (`loadFailure(query)`), the bars give way to the reason and a Retry — and the
 * editor stays out of reach, which matters: a form that opened blank on a failed load and was
 * saved would overwrite the item with nothing.
 *
 * `width` is the page's own (`Page`): the same as the loaded page, so nothing moves when it arrives.
 */
export function FormPlaceholder({
  header,
  width = "form",
  failure,
}: {
  header: ReactNode;
  width?: "list" | "form";
  failure?: LoadFailure;
}) {
  return (
    <Page width={width}>
      {header}
      {failure ? (
        <div className="max-w-form">
          <ErrorBox
            error={failure.error}
            title="Could not load this item"
            onRetry={failure.onRetry}
            retrying={failure.retrying}
          />
        </div>
      ) : (
        <LoadingRegion className="card max-w-form space-y-5">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="space-y-2">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </LoadingRegion>
      )}
    </Page>
  );
}
